// AmsGlossary.tsx — the words this module uses, in plain English.
//
// NOT DECORATION. Arrears work is done by four teams with different training, and the same word
// means different things to each: "months overdue" to a finance officer is a ratio, to a Property
// Officer it is how long since someone last paid. A shared glossary is cheaper than the meeting
// that discovers the mismatch.
//
// TWO ENTRIES EARN THIS SCREEN ON THEIR OWN — "days overdue" and "months of charge". They are two
// different clocks and conflating them is how a Town Council reports 3% when the real figure is
// twenty. Every other definition here is convenience; those two are a defence.
//
// Written against what the code actually does. If a definition here and the behaviour diverge,
// the definition is the bug report.

interface Entry { term: string; meaning: string; careful?: string }

const TERMS: Entry[] = [
  {
    term: 'Days overdue',
    meaning: 'How long the oldest unpaid charge has been outstanding, after payments are applied ' +
             'oldest-first. This is the clock everything acts on: the 30, 60 and 90 day bands, ' +
             'every letter, every visit.',
    careful: 'This is the legal and operational basis. If a letter rests on anything, it rests ' +
             'on this.',
  },
  {
    term: 'Months of charge',
    meaning: 'The balance divided by the monthly service and conservancy charge. A ratio, not a ' +
             'duration — a household owing three months of charges may have been overdue for one ' +
             'month or for two years.',
    careful: 'Reported to MND, and it triggers NOTHING. It is shown beside days overdue precisely ' +
             'so the two are never mistaken for each other.',
  },
  {
    term: 'MND measure',
    meaning: 'Accounts more than 90 days overdue divided by the WHOLE account base — not by the ' +
             'arrears population. Green band is under 4%; the Town Council’s own target is 2%.',
    careful: 'The denominator is on screen every time it is shown. Quoted against the arrears ' +
             'population instead, a 4% figure becomes 20%.',
  },
  { term: 'TCMS', meaning:
    'The Town Council’s finance system. It holds the money: charges, payments, balances, and it ' +
    'is what actually sends letters and messages. TownForge decides and records; TCMS moves ' +
    'money and sends.' },
  { term: 'Category 1 — Hardship', meaning:
    'The household cannot pay. Visited to understand, not to collect. Always carries a driver ' +
    '(1A–1F), because the driver decides the treatment.' },
  { term: 'Category 2 — Lapsed', meaning:
    'They forgot, or a GIRO arrangement broke. Usually resolved by a reminder or a call.' },
  { term: 'Category 3 — Chronic', meaning:
    'They can pay and choose not to. This is the group the legal ladder exists for.' },
  { term: 'Category 4 — Deceased or vacant', meaning:
    'No household to pursue. Handled with HDB rather than by collection.' },
  { term: '1A to 1F', meaning:
    'The driver behind a hardship case: 1A unemployment, 1B elderly and unable to work, ' +
    '1C low-income family, 1D medical condition, 1E bankruptcy, 1F lacks mental capacity.' },
  {
    term: 'Suppression',
    meaning: 'A recorded decision that this household is protected from some or all stages, under ' +
             'a named rule, by a named officer, until a stated date.',
    careful: 'Held by TownForge, not by TCMS — because TownForge is what triggers a send, so it ' +
             'is the only place a stop can actually stop anything.',
  },
  { term: 'Plan-conditional', meaning:
    'Fee-bearing stages pause while an instalment plan is kept, and resume if it is not.' },
  { term: 'Deferral', meaning:
    'Payment postponed for a set period. Document stages may continue as statements; demands do ' +
    'not.' },
  { term: 'Hard stop', meaning:
    'No notices, no fees, no legal action at all. Applies to bankruptcy (1E) and lack of mental ' +
    'capacity (1F).' },
  {
    term: 'Hand-off to the CLT',
    meaning: 'A Property Officer who sees hardship passes the case to the Community Liaison Team ' +
             'and fee-bearing stages pause immediately.',
    careful: 'The PO does not classify. Protection is applied on suspicion; the category waits ' +
             'for evidence.',
  },
  { term: 'PO — Property Officer', meaning:
    'Visits Categories 2–4 to collect and confirm. Sees hardship, hands it over.' },
  { term: 'CLT — Community Liaison Team', meaning:
    'Visits Category 1. Assesses circumstances, records the driver and evidence, agrees a plan, ' +
    'makes referrals.' },
  { term: 'Call centre', meaning:
    'Rings residents on a ranked list and records what was said. Records; does not decide.' },
  {
    term: 'Promise to pay',
    meaning: 'A date the resident gave. The system notices when it passes without payment.',
    careful: 'A promise with no date is refused — it cannot be followed up, so it is not a ' +
             'promise.',
  },
  { term: 'Consent to share', meaning:
    'The resident agreeing that their circumstances may be disclosed to an agency such as SSO. ' +
    'Recorded and timestamped, because a referral without it discloses personal circumstances ' +
    'to a third party with no basis.' },
  { term: 'SSO, SSA, VWO, MediFund', meaning:
    'Social Service Office, Social Service Agency, Voluntary Welfare Organisation, and the fund ' +
    'for medical bills. Where a hardship household is referred for help.' },
  { term: 'Demand letter', meaning:
    'A formal letter through the law firm. First and second demand come before a summons, and ' +
    'each adds a fee to the account.' },
  { term: 'Summons', meaning: 'A court document beginning legal action.' },
  { term: 'Trigger and confirm', meaning:
    'TownForge asks TCMS to send something and waits to be told it went. "Asked, and do not know" ' +
    'is its own state and is shown as such — it is the failure mode that matters.' },
  { term: 'Priority score', meaning:
    'How a queue is ordered. Category 1 cases are ordered by urgency; everything else by ' +
    'likelihood of recovery. Every row says which, because ranking hardship by likelihood of ' +
    'payment would push the least able to pay to the bottom.' },
  { term: 'Division', meaning: 'A constituency area within the Town Council: NSC, NSE, CP, NSL, NSS.' },
]

export default function AmsGlossary() {
  return (
    <div className="ams">
      <header className="ams-head">
        <div>
          <h2>Glossary</h2>
          <p className="muted">
            The words this module uses, and what they mean here. Where a term is easy to misread,
            the entry says how.
          </p>
        </div>
      </header>

      <section className="card">
        <dl className="gloss">
          {TERMS.map(t => (
            <div key={t.term} className="gloss-row">
              <dt>{t.term}</dt>
              <dd>
                {t.meaning}
                {/* The trap, where there is one. Set apart because a warning buried in a
                    definition is a warning nobody reads. */}
                {t.careful && <div className="gloss-careful">{t.careful}</div>}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  )
}
